import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth, hasRole } from "@/lib/auth-context";
import { ThemeProvider } from "@/lib/theme-context";
import { LanguageProvider } from "@/lib/i18n-context";
import { ShiftProvider } from "@/lib/shift-context";
import { ShiftPickerModal } from "@/components/ShiftPicker";
import { AppLayout } from "@/components/AppLayout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { FeedbackButton } from "@/components/FeedbackButton";
import { DemoBanner } from "@/components/DemoBanner";
import { Toaster } from "sonner";

// Eager-loaded (critical / small): auth, layout entry points, dashboards.
import LoginPage from "@/pages/Login";
import ChangePasswordPage from "@/pages/ChangePassword";
import ForgotPasswordPage from "@/pages/ForgotPassword";
import ResetPasswordPage from "@/pages/ResetPassword";
import OwnerDashboard from "@/pages/OwnerDashboard";
import ProductionCatalog from "@/pages/ProductionCatalog";
import ProductionReport from "@/pages/ProductionReport";
import ProductionOverview from "@/pages/ProductionOverview";
import ManagerDashboard from "@/pages/ManagerDashboard";
import EmployeeDashboard from "@/pages/EmployeeDashboard";
import WarehouseDashboard from "@/pages/WarehouseDashboard";
import AcceptInvite from "@/pages/AcceptInvite";

// Lazy-loaded — each becomes its own chunk, loaded only when the user navigates
// there. Massively speeds up initial dashboard render (fewer JS bytes to parse).
const DailyReconciliations = lazy(() => import("@/pages/DailyReconciliations"));
const ReconciliationDetail = lazy(() => import("@/pages/ReconciliationDetail"));
const CashReconciliationPage = lazy(() => import("@/pages/Placeholders"));
const Vouchers = lazy(() => import("@/pages/Vouchers"));
const VoucherSettings = lazy(() => import("@/pages/VoucherSettings"));
const ShiftChecklists = lazy(() => import("@/pages/ShiftChecklists"));
const Expenses = lazy(() => import("@/pages/Expenses"));
const Alerts = lazy(() => import("@/pages/Alerts"));
const AuditLog = lazy(() => import("@/pages/AuditLog"));
const Settings = lazy(() => import("@/pages/Settings"));
const ReconSettings = lazy(() => import("@/pages/ReconSettings"));
const StaffAdmin = lazy(() => import("@/pages/StaffAdmin"));
const Feedback = lazy(() => import("@/pages/Feedback"));
const Categories = lazy(() => import("@/pages/Categories"));
const DataQuality = lazy(() => import("@/pages/DataQuality"));
const Users = lazy(() => import("@/pages/Users"));
const Orders = lazy(() => import("@/pages/Orders"));
const KitchenOrder = lazy(() => import("@/pages/KitchenOrder"));
const Products = lazy(() => import("@/pages/Products"));
const Suppliers = lazy(() => import("@/pages/Suppliers"));
const Deliveries = lazy(() => import("@/pages/Deliveries"));
const Invoices = lazy(() => import("@/pages/Invoices"));
const Reports = lazy(() => import("@/pages/Reports"));
const Payroll = lazy(() => import("@/pages/Payroll"));
const Safes = lazy(() => import("@/pages/Safes"));

function LazyFallback() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center text-[var(--tt-muted)] text-sm">
      Ładuję…
    </div>
  );
}

function Protected({ children, roles }) {
  const { user, loading, forcePasswordChange } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[var(--tt-muted)]">Ładowanie…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (forcePasswordChange) return <Navigate to="/change-password" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function DashboardRouter() {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role === "owner") return <OwnerDashboard />;
  if (user.role === "manager") return <ManagerDashboard />;
  if (user.role === "accounting" || user.role === "readonly") return <OwnerDashboard />;
  if (user.role === "cook") return <Navigate to="/kitchen" replace />;
  // P2e (2026-07-05) — Warehouse (magazyn) ma dedykowany dashboard: 4 kafelki
  // (Zamówienia, Checklista, Raport produkcji, Produkcja dziś) + i18n PL/TH/RU.
  // Bez „Rozliczenia dnia" które magazyn nie prowadzi.
  if (user.role === "warehouse") return <WarehouseDashboard />;
  return <EmployeeDashboard />;
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
        <LanguageProvider>
        <ShiftProvider>
        <BrowserRouter>
          <DemoBanner />
          {/* Global Toaster — dostępny na wszystkich routes (również public: login, accept-invite,
              reset-password, forgot-password, kitchen, production/report). Wcześniej był tylko
              w AppLayout, więc na public routes toasty były niewidoczne (bug „nic się nie dzieje"). */}
          <Toaster
            position="top-right"
            closeButton
            duration={5000}
            toastOptions={{
              classNames: {
                toast: "tt-toast group !bg-[var(--tt-surface)] !text-[var(--tt-text)] !border !border-[var(--tt-border)] !shadow-lg",
                success: "!bg-[var(--tt-success-bg)] !text-[var(--tt-success-fg)] !border-[var(--tt-success-border)]",
                error: "!bg-[var(--tt-critical-bg)] !text-[var(--tt-critical-fg)] !border-[var(--tt-critical-border)]",
                warning: "!bg-[var(--tt-warn-bg)] !text-[var(--tt-warn-fg)] !border-[var(--tt-warn-border)]",
                info: "!bg-[var(--tt-info-bg)] !text-[var(--tt-info-fg)] !border-[var(--tt-info-border)]",
                title: "tt-display font-semibold",
                description: "text-sm opacity-90",
                actionButton: "!bg-[var(--tt-primary)] !text-[var(--tt-primary-fg)]",
                cancelButton: "!bg-[var(--tt-surface-2)] !text-[var(--tt-text)]",
                closeButton: "!bg-[var(--tt-surface-2)] !text-[var(--tt-text)] !border-[var(--tt-border)]",
              },
            }}
          />
          <Suspense fallback={<LazyFallback />}>
          <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/accept-invite/:token" element={<AcceptInvite />} />
          <Route path="/change-password" element={<ChangePasswordRoute />} />
          <Route path="/kitchen" element={<Protected roles={["cook", "owner", "manager", "warehouse"]}><KitchenOrder /></Protected>} />
          <Route path="/production/report" element={<Protected roles={["cook", "warehouse", "owner", "manager"]}><ProductionReport /></Protected>} />
          <Route element={<Protected><AppLayout /></Protected>}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardRouter />} />
            <Route path="/daily" element={<Protected roles={["owner", "manager", "waitress", "bartender", "receptionist", "accounting", "readonly"]}><DailyReconciliations /></Protected>} />
            <Route path="/daily/:id" element={<Protected roles={["owner", "manager", "waitress", "bartender", "receptionist", "accounting", "readonly"]}><ReconciliationDetail /></Protected>} />
            <Route path="/cash" element={<Protected roles={["owner", "manager", "accounting"]}><CashReconciliationPage /></Protected>} />
            <Route path="/vouchers" element={<Protected roles={["owner", "manager", "bartender", "receptionist"]}><Vouchers /></Protected>} />
            <Route path="/expenses" element={<Protected roles={["owner", "manager", "accounting", "waitress", "bartender", "receptionist"]}><Expenses /></Protected>} />
            <Route path="/orders" element={<Protected roles={["owner", "manager", "bartender", "cook", "warehouse", "accounting"]}><Orders /></Protected>} />
            <Route path="/products" element={<Protected roles={["owner", "manager", "accounting"]}><Products /></Protected>} />
            <Route path="/production" element={<Protected roles={["owner", "manager", "accounting"]}><ProductionCatalog /></Protected>} />
            <Route path="/production/overview" element={<Protected roles={["owner", "manager", "accounting", "warehouse"]}><ProductionOverview /></Protected>} />
            <Route path="/suppliers" element={<Protected roles={["owner", "manager", "accounting"]}><Suppliers /></Protected>} />
            <Route path="/deliveries" element={<Protected roles={["owner", "manager", "warehouse", "accounting"]}><Deliveries /></Protected>} />
            <Route path="/invoices" element={<Protected roles={["owner", "manager", "accounting"]}><Invoices /></Protected>} />
            <Route path="/alerts" element={<Protected roles={["owner", "manager", "accounting"]}><Alerts /></Protected>} />
            <Route path="/audit" element={<Protected roles={["owner", "accounting"]}><AuditLog /></Protected>} />
            <Route path="/settings" element={<Protected roles={["owner"]}><Settings /></Protected>} />
            <Route path="/recon-settings" element={<Protected roles={["owner"]}><ReconSettings /></Protected>} />
            <Route path="/voucher-settings" element={<Protected roles={["owner"]}><VoucherSettings /></Protected>} />
            <Route path="/shift-checklists" element={<Protected roles={["owner", "manager", "waitress", "bartender", "receptionist", "cook"]}><ShiftChecklists /></Protected>} />
            <Route path="/users" element={<Protected roles={["owner", "manager"]}><StaffAdmin /></Protected>} />
            <Route path="/feedback" element={<Protected roles={["owner"]}><Feedback /></Protected>} />
            <Route path="/categories" element={<Protected roles={["owner", "manager"]}><Categories /></Protected>} />
            <Route path="/data-quality" element={<Protected roles={["owner", "manager"]}><DataQuality /></Protected>} />
            <Route path="/users-legacy" element={<Protected roles={["owner"]}><Users /></Protected>} />
            <Route path="/reports" element={<Protected roles={["owner", "manager", "accounting"]}><Reports /></Protected>} />
            <Route path="/payroll" element={<Protected roles={["owner", "accounting"]}><Payroll /></Protected>} />
            <Route path="/safes" element={<Protected roles={["owner", "manager", "accounting"]}><Safes /></Protected>} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        </Suspense>
        <AuthedFeedbackButton />
        <AuthedShiftPickerModal />
        </BrowserRouter>
        </ShiftProvider>
        </LanguageProvider>
      </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

function AuthedFeedbackButton() {
  const { user } = useAuth();
  if (!user) return null;
  return <FeedbackButton />;
}

/**
 * P2h (2026-07-10) — pokazuje modal wyboru działu tylko dla zalogowanych
 * multi-department userów którzy jeszcze nie wybrali działu na dziś.
 */
function AuthedShiftPickerModal() {
  const { user } = useAuth();
  if (!user) return null;
  return <ShiftPickerModal />;
}

function ChangePasswordRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <ChangePasswordPage />;
}

export default App;
